import React from 'react';
import { QuickPickName } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPick/QuickPickName';
import InlineScroll from 'src/components/InlineScroll';
import { observer } from 'src/utils/mobx-react';
import { QuickPickWrapper } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPick/QuickPick.style';
import { RaceWinnerColumn } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/RaceWinner/RaceWinnerColumn';
import { QuickPickViewState } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/QuickPickView.state';
import { MainMarkets } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/MainMarkets/MainMarkets';
import { ForecastTricast } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/ForecastTricast/ForecastTricast';
import { ExtraPlaces } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/ExtraPlaces/ExtraPlaces';
import { MatchBet } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/MatchBet/MatchBet';
import { LengthenTheOdds } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/LengthenTheOdds';
import { assertNever } from 'src_common/common/assertNever';
import { MARKET_TYPE } from 'src_common/common/websocket2/models/EventModel';
import { WinOnly } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/WinOnly/WinOnly';
import { PlaceOnly } from 'src/components/CustomersBar/EventsTab/RacingTab/QuickPickView/PlaceOnly/PlaceOnly';

interface MarketsForSelectedEventPropsType {
    eventId: number;
    userId: number;
    quickPickState: QuickPickViewState;
}

const MarketsForSelectedEvent = observer('MarketsForSelectedEvent', (props: MarketsForSelectedEventPropsType) => {
    const state = props.quickPickState;

    const viewContent = (): JSX.Element | null => {
        switch (state.marketFilterState.getMarketType) {
            case MARKET_TYPE.RACE_WINNER:
                return (
                    <MainMarkets
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.FORECAST:
                return (
                    <ForecastTricast
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.LENGTHEN_THE_ODDS:
                return (
                    <LengthenTheOdds
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.EXTRA_PLACES:
                return (
                    <ExtraPlaces
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.MATCH_BET:
                return (
                    <MatchBet
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.SUB_RACE:
                return (
                    <MatchBet
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.WITHOUT:
                return (
                    <MatchBet
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.WIN_ONLY:
                return (
                    <WinOnly
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
            case MARKET_TYPE.TOP_2_FINISH:
            case MARKET_TYPE.TOP_3_FINISH:
            case MARKET_TYPE.TOP_4_FINISH:
            case MARKET_TYPE.TOP_5_FINISH:
            case MARKET_TYPE.TOP_6_FINISH:
                return (
                    <PlaceOnly
                        userId={props.userId}
                        quickPickState={state}
                    />
                );
        }

        return assertNever('markets in Telebet', state.marketFilterState.getMarketType);
    };

    return (
        <>
            {viewContent()}
            <button
                key='close'
                className='btn events-tab--horse-racing__close-racecard'
                onClick={state.onCloseRacecard}
            >
                ×
            </button>
        </>
    );
});

type QuickPickTypePropsType = {
    userId: number;
    quickPickState: QuickPickViewState;
};

export const QuickPick = observer('QuickPick', (props: QuickPickTypePropsType) => {
    const buildContent = (): JSX.Element | null => {
        if (props.quickPickState.getSelectedEvent === null) {
            return null;
        }

        const currentMarket =
            props.quickPickState.competitionFilterState.selectedEventIdFromURL === null
                ? props.quickPickState.marketFilterState.raceWinnerMarket
                : props.quickPickState.marketFilterState.marketModelForSelectedMarket;

        const renderMarketsForSelectedEvent = (): JSX.Element | null => {
            return props.quickPickState.competitionFilterState.selectedEventIdFromURL === null ? null : (
                <MarketsForSelectedEvent
                    eventId={props.quickPickState.competitionFilterState.selectedEventIdFromURL}
                    userId={props.userId}
                    quickPickState={props.quickPickState}
                />
            );
        };

        return (
            <div className='quick-pick__content'>
                <QuickPickName
                    eventId={props.quickPickState.getSelectedEvent.id}
                    marketId={currentMarket?.id ?? null}
                    quickPickState={props.quickPickState}
                />

                <InlineScroll
                    resetScroll={false}
                    scrollData={{ className: '.race-winner-column' }}
                >
                    {renderMarketsForSelectedEvent() ?? (
                        <div className='quick-pick__table'>
                            {props.quickPickState.getEventsList.map((event) => {
                                const market = event.marketRaceWinner[0];

                                if (market === undefined) {
                                    return null;
                                }

                                return (
                                    <RaceWinnerColumn
                                        userId={props.userId}
                                        key={`${event.id}-${market.id}`}
                                        eventId={event.id}
                                        marketId={market.id}
                                        selected={props.quickPickState.hoveredEventId === event.id}
                                        quickPickState={props.quickPickState}
                                    />
                                );
                            })}
                        </div>
                    )}
                </InlineScroll>
            </div>
        );
    };

    return (
        <QuickPickWrapper className='quick-pick'>
            {props.quickPickState.getEventsList.length === 0 ? (
                <div className='quick-pick__info'>
                    There are no races available at the moment. Please check again later.
                </div>
            ) : (
                buildContent()
            )}
        </QuickPickWrapper>
    );
});
